import { useUrlState } from '@/hooks/useUrlState'

/** The recipient a wizard draft is for — the `customerId` of the eventual `POST /offers`. */
export interface DraftRecipient {
	id: string
	name: string
}

/** The `recipient` query parameter: no recipient when absent (TO-652 REQ-007). */
const NO_RECIPIENT: DraftRecipient | null = null

/** Parse the `recipient` parameter back into a draft recipient; reject anything that is not `{id,name}`. */
function parseRecipient(raw: string): DraftRecipient | null | undefined {
	try {
		const value = JSON.parse(raw) as unknown
		if (value && typeof value === 'object' && 'id' in value && 'name' in value) {
			const { id, name } = value as Record<string, unknown>
			if (typeof id === 'string' && typeof name === 'string') return { id, name }
		}
	} catch {
		// Garbage in the URL falls back to no recipient (url-state read contract).
	}
	return undefined
}

/** Serialize a draft recipient for the URL; the empty string (no recipient) drops the parameter. */
function serializeRecipient(value: DraftRecipient | null): string {
	return value ? JSON.stringify(value) : ''
}

/**
 * The offer wizard's client-side draft (TO-122). Step 1 sets the recipient; the
 * later steps (TO-123/124/125) and the shell lift this into the wizard. The draft
 * persists server-side only when the final step creates the offer.
 *
 * @remarks
 * TO-652 REQ-007: Step 1 runs at `/offers/new` before a draft (and its route)
 * exists, so the picked recipient is reflected in the `recipient` query parameter
 * via {@link useUrlState}. Reloading or returning with browser Back restores the
 * selection instead of clearing it; the route-per-step model still owns every
 * saved step from Step 2 on. Default (push) history is deliberately avoided —
 * selecting a recipient should not add a Back step before Step 1 is left.
 */
export function useOfferWizardDraft() {
	const [recipient, setRecipient] = useUrlState<DraftRecipient | null>('recipient', {
		defaultValue: NO_RECIPIENT,
		parse: parseRecipient,
		serialize: serializeRecipient,
	})
	return { recipient, setRecipient }
}
